import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api, formatErr } from "@/lib/api";
import { CheckCircle2, DollarSign, Loader2 } from "lucide-react";

/** Confirmation page after Stripe checkout for a "Send $" tip. */
export default function TipSuccess() {
  const [sp] = useSearchParams();
  const sessionId = sp.get("session_id");
  const guestToken = sp.get("t");
  const [tip, setTip] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!sessionId) { setErr("Missing session id."); return; }
    const q = guestToken ? `?t=${encodeURIComponent(guestToken)}` : "";
    let cancelled = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/tips/status/${sessionId}${q}`);
        if (cancelled) return;
        setTip(data);
        if (data.payment_status !== "paid") setTimeout(poll, 1500);
      } catch (e) { if (!cancelled) setErr(formatErr(e)); }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, guestToken]);

  if (err) return (
    <div className="max-w-md mx-auto p-10 text-center" data-testid="tip-success-error">
      <p className="text-[#B84B4B]">{err}</p>
      <Link to="/" className="btn-secondary mt-4 inline-flex">Back home</Link>
    </div>
  );
  if (!tip) return (
    <div className="max-w-md mx-auto p-10 text-center" data-testid="tip-success-loading">
      <Loader2 className="animate-spin mx-auto" size={22}/>
      <p className="text-[#8A8A8A] mt-3">Confirming your payment…</p>
    </div>
  );

  const paid = tip.payment_status === "paid";
  return (
    <div className="max-w-md mx-auto px-6 py-16 text-center" data-testid="tip-success-page">
      <div className={`h-14 w-14 mx-auto rounded-full inline-flex items-center justify-center ${paid ? "bg-[#0057FF] text-white" : "bg-[#F2F2F0] text-[#4A4A4A]"}`}>
        {paid ? <CheckCircle2 size={26}/> : <DollarSign size={26}/>}
      </div>
      <h1 className="h-display text-4xl mt-4">{paid ? "Sent." : "Almost there…"}</h1>
      <p className="text-[#4A4A4A] mt-2">
        {paid
          ? <>You sent <span className="font-heading">${Number(tip.amount).toFixed(2)}</span> to {tip.recipient_name || "them"}.</>
          : "Waiting on Stripe to confirm your payment."}
      </p>
      {tip.message && (
        <div className="card p-4 mt-6 text-left" data-testid="tip-message-echo">
          <p className="overline mb-1">Your note</p>
          <p className="text-sm">{tip.message}</p>
        </div>
      )}
      <Link to={`/u/${tip.recipient_id}`} className="btn-primary mt-8 inline-flex" data-testid="tip-success-back">
        Back to profile
      </Link>
    </div>
  );
}
